import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import axios from "axios";
import {
  ArrowLeft,
  Calendar,
  User,
  Tag,
  Share2,
  BookOpen,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default article content
const defaultArticleContent = `
## Introduction

Le choix du statut juridique est une décision cruciale pour tout freelance. Ce guide vous aide à comprendre les différentes options et à faire le meilleur choix pour votre situation.

## Les différents statuts disponibles

### Micro-entreprise (Auto-entrepreneur)

La micro-entreprise est souvent le premier choix des freelances débutants. Simple à créer et à gérer, elle offre :

- Une comptabilité simplifiée
- Des charges calculées sur le chiffre d'affaires réel
- Pas de TVA (sous certains seuils)

### EURL (Entreprise Unipersonnelle à Responsabilité Limitée)

L'EURL convient aux freelances qui souhaitent :

- Protéger leur patrimoine personnel
- Déduire leurs charges professionnelles
- Avoir plus de flexibilité dans leur rémunération

### SASU (Société par Actions Simplifiée Unipersonnelle)

La SASU est idéale pour :

- Bénéficier du régime général de la sécurité sociale
- Optimiser la rémunération via dividendes
- Projeter une image professionnelle

## Comment choisir ?

Le choix dépend de plusieurs facteurs :

1. **Votre chiffre d'affaires prévisionnel** - La micro-entreprise a des plafonds
2. **Vos charges professionnelles** - Déductibles en société uniquement
3. **Votre situation personnelle** - Couverture sociale, projets futurs
4. **Vos objectifs de croissance** - Embauche, investissements

## Conclusion

N'hésitez pas à utiliser notre simulateur pour comparer les différents statuts selon votre situation personnelle. Pour un accompagnement personnalisé, notre équipe est à votre disposition.
`;

export default function BlogArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/blog/articles/slug/${slug}`);
      setArticle(response.data);
    } catch (error) {
      console.error("Error fetching article:", error);
      // Set default article based on slug
      setArticle({
        id: "1",
        title: slug.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
        slug: slug,
        excerpt: "Découvrez nos conseils et guides pratiques pour les freelances.",
        content: defaultArticleContent,
        category: "Statuts juridiques",
        image_url: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200",
        author: "Alexandre Martin",
        published: true,
        created_at: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Lien copié dans le presse-papier !");
    }
  };

  const renderContent = (content) => {
    // Simple markdown-like rendering
    const lines = content.split("\n");
    const elements = [];
    let currentList = [];

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      if (trimmed.startsWith("## ")) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 mb-4 space-y-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <h2 key={index} className="font-manrope font-bold text-2xl text-slate-900 mt-8 mb-4">
            {trimmed.replace("## ", "")}
          </h2>
        );
      } else if (trimmed.startsWith("### ")) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 mb-4 space-y-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <h3 key={index} className="font-manrope font-semibold text-xl text-slate-900 mt-6 mb-3">
            {trimmed.replace("### ", "")}
          </h3>
        );
      } else if (trimmed.startsWith("- ")) {
        currentList.push(
          <li key={index} className="text-slate-600">
            {trimmed.replace("- ", "")}
          </li>
        );
      } else if (trimmed.match(/^\d+\./)) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 mb-4 space-y-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <p key={index} className="text-slate-600 mb-2 pl-4">
            <strong>{trimmed.split(" ")[0]}</strong> {trimmed.split(" ").slice(1).join(" ")}
          </p>
        );
      } else if (trimmed) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`} className="list-disc pl-6 mb-4 space-y-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <p key={index} className="text-slate-600 mb-4 leading-relaxed">
            {trimmed}
          </p>
        );
      }
    });

    if (currentList.length > 0) {
      elements.push(<ul key="list-final" className="list-disc pl-6 mb-4 space-y-2">{currentList}</ul>);
    }

    return elements;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-12" data-testid="blog-article-loading">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Skeleton className="h-8 w-32 mb-8" />
          <Skeleton className="h-64 w-full rounded-2xl mb-8" />
          <Skeleton className="h-10 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-50 py-20" data-testid="blog-article-not-found">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h1 className="font-manrope font-bold text-2xl text-slate-900 mb-4">
            Article non trouvé
          </h1>
          <p className="text-slate-600 mb-8">
            L'article que vous recherchez n'existe pas ou a été supprimé.
          </p>
          <Link to="/blog">
            <Button className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] rounded-full">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-article-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour au blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          {/* Featured Image */}
          {article.image_url && (
            <div className="aspect-video rounded-2xl overflow-hidden mb-8">
              <img
                src={article.image_url}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <Badge variant="secondary" className="bg-[#52fbb0]/10 text-slate-700">
              <Tag className="h-3 w-3 mr-1" />
              {article.category}
            </Badge>
            <span className="text-sm text-slate-500 flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {formatDate(article.created_at)}
            </span>
            <span className="text-sm text-slate-500 flex items-center gap-1">
              <User className="h-4 w-4" />
              {article.author}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-slate-900 mb-6">
            {article.title}
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-slate-600 mb-8 pb-8 border-b border-slate-200">
            {article.excerpt}
          </p>

          {/* Content */}
          <div className="prose max-w-none">
            {renderContent(article.content || defaultArticleContent)}
          </div>

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-900 mb-1">Partagez cet article</p>
                <p className="text-sm text-slate-500">Aidez d'autres freelances à découvrir ces conseils</p>
              </div>
              <Button
                variant="outline"
                onClick={handleShare}
                className="rounded-full"
                data-testid="share-button"
              >
                <Share2 className="mr-2 h-4 w-4" />
                Partager
              </Button>
            </div>
          </div>

          {/* CTA */}
          <Card className="mt-12 bg-gradient-to-br from-[#52fbb0]/10 to-[#34d399]/10 border-[#52fbb0]/20">
            <CardContent className="p-8 text-center">
              <h3 className="font-manrope font-bold text-2xl text-slate-900 mb-3">
                Besoin d'un accompagnement personnalisé ?
              </h3>
              <p className="text-slate-600 mb-6">
                Notre équipe d'experts est là pour vous aider à optimiser votre statut et maximiser vos revenus.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/simulateur">
                  <Button className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] rounded-full px-8">
                    Essayer le simulateur
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="rounded-full px-8">
                    Nous contacter
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </article>
    </div>
  );
}
